import React, { ReactElement, useContext, useEffect, useRef, useState } from "react";
import cls from "classnames";
import { AsynchronousNotification, AsynchronousNotificationAction, AsynchronousNotificationWeight } from "../../../models/AsynchronousNotification";
import FontAwesomeIcon from "../fa_icon";
import styles from "./AsynchronousNotificationList.module.scss";
import moment from "moment";
import { getOriginalDocumentTitle } from "../DocumentTitle/DocumentTitle";
import { Button, ButtonProps } from "../Button/Button";
import { truncateKeepWords } from "../../../utils";
import { TelephonyContext } from "../../telephony/TelephonyContext";
import { isEventFromCurrentTarget } from "../../../utils/dom";
import { createPortal } from "react-dom";

type Props = {
  notifications: AsynchronousNotification[];
  onClose: (notification: AsynchronousNotification) => void;
}

/**
 * Renders a list of notifications which can appear asynchronously,
 * unlike ActionNotification which appears immediately to notify the result of an action.
 */
export const AsynchronousNotificationList: React.FC<Props> = ({ notifications, onClose }) => {
  const root = useRef(document.getElementById('notifications'));

  useEffect(() => {
    //  Latest notification first.
    const latestNotificationWithDocumentTitle = notifications.find(notification => !notification.isRead && notification.documentTitle);
    (window as AsynchronousNotificationWindow).asyncNotificationFlashingDocumentTitle = latestNotificationWithDocumentTitle?.documentTitle;

    if (latestNotificationWithDocumentTitle) {
      document.title = latestNotificationWithDocumentTitle.documentTitle;
    }
  }, [notifications]);

  return createPortal((
    <div className={styles.listContainer}>
      <div role="list" className={styles.list}>
        {
          notifications.map(notification => (
            <AsynchronousNotificationListItem
              key={notification.key}
              notification={notification}
              onClose={() => onClose(notification)} />
          ))
        }
      </div>
    </div>
  ), root.current);
}

type ListItemProps = {
  notification: AsynchronousNotification;
  onClose: () => void;
}

type AsynchronousNotificationWindow = typeof window & {
  asyncNotificationFlashingDocumentTitle?: string;
};

const documentTitleInterval = moment.duration(2.5, 'seconds').asMilliseconds();

//  Periodically flash latest document title.
setInterval(() => {
  if ((window as AsynchronousNotificationWindow).asyncNotificationFlashingDocumentTitle) {
    document.title = (window as AsynchronousNotificationWindow).asyncNotificationFlashingDocumentTitle;
  }

  setTimeout(() => {
    document.title = getOriginalDocumentTitle();
  }, documentTitleInterval);
}, documentTitleInterval * 2);

export const asyncNotificationsMaxCollapsedBodyLength = 100;

const AsynchronousNotificationListItem: React.FC<ListItemProps> = ({ notification, onClose }) => {
  const updatePositionIntervalId = useRef(null);

  const body = typeof notification.body === 'function' ? notification.body() : notification.body;

  //  We briefly make the notification non-interactive so that the user does not accidentally click on it.
  const [isInteractive, setInteractive] = useState(false);
  const isExpandable = !notification.actions.default && typeof body === 'string' &&
    body.length > asyncNotificationsMaxCollapsedBodyLength;
  const [isExpanded, setExpanded] = useState(!isExpandable);
  const [isDismissing, setDismissing] = useState(false);
  const [_, setNonce] = useState(Date.now());
  const [isHidden, setHidden] = useState(false);
  const [hoverAction, setHoverAction] = useState<'default' | 'dismiss' | 'confirm'>('default');
  const telephonyContext = useContext(TelephonyContext);

  useEffect(() => {
    if (!isExpandable) {
      setExpanded(true);
    }
  }, [isExpandable]);

  useEffect(() => {
    const interactiveTimeoutId = setTimeout(() => setInteractive(true), 750);
    return () => { clearTimeout(interactiveTimeoutId); }
  }, []);

  useEffect(() => {
    if (typeof notification.body !== 'function' || !notification.bodyUpdateInterval) {
      return;
    }

    const intervalId = setInterval(() => setNonce(Date.now()), notification.bodyUpdateInterval);
    return () => { clearInterval(intervalId); }
  }, [notification]);

  async function dismiss() {
    if (notification.actions.dismiss?.onClick) {
      setDismissing(true);

      try {
        await notification.actions.dismiss.onClick({
          telephonyContext: telephonyContext,
          notification
        });
      } catch (error) {
        console.error("Error executing onClick on dismiss:", error);
      }

      setDismissing(false);
    }

    onClose();
  }

  if (isHidden) {
    return null;
  }

  return (
    <div
      role="listitem"
      className={cls([
        styles.item,
        isExpandable ? styles.expandable : null,
        notification.isRead ? styles.read : null,
        notification.hasAction ? styles.hasActions : null,
        notification.actions.default || (isExpandable && !isExpanded) ? styles.interactable : null,
      ])}
      data-weight={notification.weight}
      data-default-action-weight={notification.actions.default?.weight}
      data-hover-action={hoverAction}
      onAnimationEnd={event => {
        if (isEventFromCurrentTarget(event) && notification.isRead) {
          setHidden(true);
        }
      }}>
      <div
        ref={element => {
          if (!element) {
            return;
          }

          //  Auto-move to parent.
          const parent = element.parentElement;

          function updatePosition() {
            if (!parent || !element.isConnected) {
              return;
            }

            const list = parent.parentElement;
            if (!list) {
              return;
            }

            const elementBounds = element.getBoundingClientRect();
            const parentBounds = parent.getBoundingClientRect();
            const listBounds = list.getBoundingClientRect();

            element.style.left = (parentBounds.left - listBounds.left) + 'px';
            element.style.top = (parentBounds.top - listBounds.top) + 'px';
            parent.style.width = elementBounds.width + 'px';
            parent.style.height = elementBounds.height + 'px';
          }

          updatePosition();
          clearInterval(updatePositionIntervalId.current);
          updatePositionIntervalId.current = setInterval(updatePosition, 100);

          setTimeout(() => {
            if (element?.isConnected) {
              element.classList.add(styles.afterPositionUpdated);
            }
          }, 100);
        }}
        className={styles.container1}>
        <div
          className={styles.container2}
          role={notification.actions.default ? "button" : "listitem"}
          onClick={async event => {
            if (isExpandable) {
              setExpanded(true);

            } else if (isInteractive && await notification.actions.default?.onClick({
              telephonyContext,
              notification
            }) === true) {
              onClose();
            }
          }}>
          <div className={styles.container3}>
            <div className={styles.iconContainer} data-weight={notification.icon.weight}>
              <FontAwesomeIcon
                name={notification.icon.name}
                type={notification.icon.type as any}
                className={styles.icon} />
            </div>
            <div className={styles.textContainer}>
              <div className={styles.titleContainer}>
                <h3 className={styles.title}>{notification.title}</h3>
                {
                  notification.isDismissable ? (
                    <Button
                      icon="times"
                      onClick={event => {
                        event.stopPropagation();
                        dismiss();
                      }}
                      onMouseEnter={() => setHoverAction('dismiss')}
                      onMouseLeave={() => setHoverAction('default')}
                      weight="simple"
                      disabled={isDismissing || !isInteractive} />
                  ) : null
                }
              </div>
              <div className={styles.bodyContainer}>
                <p className={styles.body}>
                  {
                    isExpanded || typeof body !== 'string' ? splitLinesIntoDivs(body) :
                      splitLinesIntoDivs(truncateKeepWords(body, asyncNotificationsMaxCollapsedBodyLength))
                  }
                  {
                    notification.actions.default && notification.actions.default?.chevron !== false ? (
                      <FontAwesomeIcon name="chevron-right" className={styles.icon} />
                    ) : isExpandable && !isExpanded ? (
                      <FontAwesomeIcon name="chevron-down" className={styles.icon} />
                    ) : null
                  }
                </p>
              </div>
            </div>
          </div>
          <div className={styles.actions}>
            <div style={{ flexGrow: 1 }}>
              {
                notification.actions.dismiss?.children ? (
                  <AsynchronousNotificationActionButton
                    action={{
                      ...notification.actions.dismiss,
                      children: notification.actions.dismiss.children!,
                      weight: notification.actions.dismiss.weight || 'dimmed'
                    }}
                    onMouseEnter={() => setHoverAction('dismiss')}
                    onMouseLeave={() => setHoverAction('default')}
                    notification={notification}
                    onClose={onClose}
                    disabled={!isInteractive} />
                ) : null
              }
            </div>
            {
              notification.actions.confirm ? (
                <AsynchronousNotificationActionButton
                  action={{
                    ...notification.actions.confirm,
                    weight: notification.actions.confirm.weight || 'simple'
                  }}
                  onMouseEnter={() => setHoverAction('confirm')}
                  onMouseLeave={() => setHoverAction('default')}
                  notification={notification}
                  onClose={onClose}
                  disabled={!isInteractive} />
              ) : null
            }
          </div>
        </div>
      </div>
    </div>
  )
}

const AsynchronousNotificationActionButton: React.FC<{
  action: AsynchronousNotificationAction;
  notification: AsynchronousNotification;
  onClose: () => void;
  onMouseEnter?: () => void;
  disabled?: boolean;
} & Pick<ButtonProps, 'onMouseEnter' | 'onMouseLeave'>> = ({ action, notification, onClose, disabled, ...buttonProps }) => {
  const [isBusy, setBusy] = useState(false);

  const telephonyContext = useContext(TelephonyContext);

  return (
    <div style={{ position: 'relative' }}>
      <Button
        {...buttonProps}
        icon={action.icon}
        iconSize={action.iconSize}
        showTaskIndicator={isBusy}
        disabled={disabled}
        onClick={async event => {
          setBusy(true);

          event.stopPropagation(); // Trigga inte actions.default.

          let ok = false;
          try {
            if (await action.onClick({ telephonyContext, notification }) === true) {
              onClose();
            }
            ok = true;
          } catch (error) {
            console.error("Asynchronous notification onClick error:", error);
          }

          if (ok) {
            onClose();
          }

          setBusy(false);
        }}
        weight={action.weight}>
        {action.children}
      </Button>
    </div>
  )
}

function splitLinesIntoDivs(element: any): ReactElement | ReactElement[] {
  if (typeof element === 'string') {
    return element.split('\n').reduce((elements, line, index, lines) => {
      elements.push(<span>{line}</span>);

      if (index < lines.length - 1) {
        //  Add a line break. There shouldn't be a line break at the very end, since we can have a button there for actions.default.
        elements.push(<br />);
      }

      return elements;
    }, [] as ReactElement[]);
  }

  return element;
}